/**
 * 作成画面の部品（#96、2026-09-29 射場さん。docs/36、Figma「現場力 画面の改善案（作成画面・タブ・戻る）9/29」）。
 * 新しい見積書（pages/EstimateNew.tsx）と新しい案件（pages/GroupNew.tsx）で同じものを使う。
 *   ・CreateBand     … 上の藍の帯（「‹ 戻り先」・題・段 3つ・図面の線画）。段を押すとそのカードへ
 *   ・StepCard       … 段ごとのカード（丸い番号：済＝藍の丸に ✓／いま＝藍の輪と番号／まだ＝灰の輪）
 *   ・Choice         … 押せるカード（role="radio"。並べる枠は .crchoices role="radiogroup"）／MoreChoice＝点線の「ほかの…」
 *   ・{customer}を選ぶのは #288 から components/PartnerPickerDrawer.tsx（右のドロアー。最近・よく使うもその中）
 *   ・PickedClientRow… 段1：選んだ{customer}（頭文字の丸・名前・敬称・支払条件）
 *   ・PreviewAside / PreviewMini / PreviewSheet … 右の「できあがり」（1100px 以上）／たたんだ形と下から出る紙（それより狭いとき）
 *   ・CreateActions  … 下の「やめる」と主役（必須がそろうまで押せない。狭い画面では主役が画面の下に付く）
 * 幅は main の枠の幅で変える（styles.css の @container create）。色は styles.css の変数だけ（帯の端の2色も変数）
 */
import { DEFAULT_PAYMENT_TERMS, partnerInitial, paymentTermsLabel, type Partner, type PartnerList, type PaymentTerms } from "@genbaryoku/contract";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api } from "../api.js";
import { useModule } from "../core/slots.jsx";
import { useLabels } from "../labels.js";
import { backTitle, type BackNav } from "./BackButton.jsx";
import { ErrorText } from "./ui.jsx";
import { LightArea } from "./LightArea.jsx";

/* ---------------- 線のアイコン（24×24。太さ・色は置いた場所の CSS） ---------------- */
type CIcon = "back" | "right" | "up" | "check" | "search" | "plus" | "eye" | "file" | "layers" | "folder" | "folderplus" | "building" | "user" | "yen" | "cal" | "pen" | "arrow" | "umbrella" | "pause" | "shift" | "warn" | "wand" | "copy" | "camera" | "clock" | "pin" | "phone" | "lock" | "link" | "close" | "image" | "download";
const PATHS: Record<CIcon, ReactNode> = {
  back: <path d="M15 18l-6-6 6-6" />,
  right: <path d="M9 6l6 6-6 6" />,
  up: <path d="M6 15l6-6 6 6" />,
  check: <path d="M20 6L9 17l-5-5" />,
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  eye: (
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  file: <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM14 3v5h5" />,
  layers: <path d="M12 3l9 5-9 5-9-5zM3 13l9 5 9-5M3 17.5l9 5 9-5" />,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  folderplus: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM12 10.5v6M9 13.5h6" />,
  building: <path d="M3 21h18M5 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M15 9h3a1 1 0 0 1 1 1v11M9 8h2M9 12h2M9 16h2" />,
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  yen: <path d="M6 4l6 8 6-8M12 12v8M7.5 13h9M7.5 16.5h9" />,
  cal: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </>
  ),
  pen: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />,
  arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
  /* 工程表（#101） */
  umbrella: <path d="M3 12a9 9 0 0 1 18 0zM12 12v7a2 2 0 0 1-4 0M12 3v0" />,
  /* 中止（営業力。雨を前に出さない＝#142 段5） */
  pause: <path d="M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18zM10 9v6M14 9v6" />,
  shift: <path d="M4 5v14M8 12h12M16 8l4 4-4 4" />,
  warn: <path d="M12 3l10 18H2zM12 10v4M12 17.5v.5" />,
  wand: <path d="M4 20l11-11M14 4l1.2 2.8L18 8l-2.8 1.2L14 12l-1.2-2.8L10 8l2.8-1.2zM19 13l.6 1.4L21 15l-1.4.6L19 17l-.6-1.4L17 15l1.4-.6z" />,
  copy: <path d="M8 8h11v13H8zM5 16H4V3h11v1" />,
  /* 協力会社へのリンクと報告（#103） */
  camera: (
    <>
      <path d="M3 8a2 2 0 0 1 2-2h2.5l1.5-2h6l1.5 2H19a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  phone: <path d="M5 3h4l2 5-2.5 1.5a11 11 0 0 0 6 6L16 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 5a2 2 0 0 1 2-2z" />,
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  link: <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />,
  download: <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 16l5-5 4 4 3-3 6 6" />
    </>
  ),
};
export function CrIcon({ k }: { k: CIcon }) {
  return (
    <svg className="cri" viewBox="0 0 24 24" aria-hidden="true">
      {PATHS[k]}
    </svg>
  );
}

/* ---------------- 藍の帯 ---------------- */
/**
 * 帯の右の図面の線画（screen_sample/design_2026-09-29/create/deco_*.svg と同じ線）。飾りなので aria-hidden。
 * <metadata>（c2pa の署名など）は入れない：入っていると Safari が描かない（#96。apps/api/test/web-styles.test.ts が確かめる）
 */
function BandDeco({ kind }: { kind: "house" | "building" | "docs" }) {
  const common = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, focusable: false } as const;
  if (kind === "docs") return <DocsDeco common={common} />;
  return (
    <span className="crdeco" aria-hidden="true">
      {kind === "house" ? (
        <svg className="pc" width="340" height="170" viewBox="0 0 340 170" strokeWidth="1.6" {...common}>
          <path d="M4 148H336" />
          <path d="M50 148V78L140 24L230 78V148" />
          <path d="M34 88L140 12L246 88" />
          <path d="M186 44V26h14v26" />
          <path d="M124 148V108H156V148" />
          <rect x="70" y="96" width="36" height="28" />
          <path d="M88 96v28M70 110h36" />
          <rect x="174" y="96" width="36" height="28" />
          <path d="M192 96v28M174 110h36" />
          <path d="M230 148V100H320V148" />
          <path d="M230 100L275 76L320 100" />
          <path d="M248 148v-32h54v32M248 126h54M248 136h54" />
          <path d="M50 160H230M50 155v10M230 155v10" strokeDasharray="3 4" />
        </svg>
      ) : (
        <svg className="pc" width="360" height="170" viewBox="0 0 360 170" strokeWidth="1.6" {...common}>
          <path d="M4 150H356" />
          <rect x="40" y="46" width="120" height="104" />
          <path d="M40 46h120M60 46V26h80v20M100 26V14" />
          <rect x="56" y="64" width="28" height="22" />
          <rect x="116" y="64" width="28" height="22" />
          <rect x="56" y="100" width="28" height="22" />
          <path d="M112 150v-40h32v40" />
          <path d="M190 150V70l60-36 60 36v80" />
          <path d="M178 78l72-50 72 50" />
          <rect x="206" y="92" width="28" height="22" />
          <rect x="266" y="92" width="28" height="22" />
          <path d="M238 150v-30h24v30" />
          <path d="M40 162H310M40 157v10M310 157v10" strokeDasharray="3 4" />
          <path d="M332 46v104M327 46h10M327 150h10" strokeDasharray="3 4" />
        </svg>
      )}
      <svg className="sp" width="170" height="90" viewBox="0 0 170 90" strokeWidth="1.4" {...common}>
        <path d="M2 84H168" />
        <path d="M26 84V44L74 14L122 44V84" />
        <path d="M16 50L74 6L132 50" />
        <path d="M66 84V62H82V84" />
        <rect x="36" y="54" width="20" height="16" />
        <rect x="92" y="54" width="20" height="16" />
        <path d="M122 84V58H164V84" />
        <path d="M122 58L143 46L164 58" />
      </svg>
    </span>
  );
}

/** 書類の線画（#142 段6 参考：家・建物は現場力＝field の会社だけ。ほかの会社は重ねた書類と表） */
function DocsDeco({ common }: { common: Record<string, unknown> }) {
  return (
    <span className="crdeco" aria-hidden="true">
      <svg className="pc" width="340" height="170" viewBox="0 0 340 170" strokeWidth="1.6" {...common}>
        <path d="M4 158H336" />
        <rect x="150" y="18" width="118" height="134" />
        <rect x="128" y="30" width="118" height="128" />
        <path d="M146 52h54M146 64h82" />
        <path d="M146 82h82M146 82v52M228 82v52M146 96h82M146 110h82M146 124h82M146 134h82M184 82v52M206 82v52" />
        <path d="M196 146h32" />
        <rect x="40" y="70" width="70" height="88" />
        <path d="M54 88h42M54 100h42M54 112h30" />
        <path d="M54 136h42M54 144h42" strokeDasharray="3 4" />
        <path d="M286 158v-48h34v48M286 122h34M286 134h34M286 146h34" />
      </svg>
      <svg className="sp" width="170" height="90" viewBox="0 0 170 90" strokeWidth="1.4" {...common}>
        <path d="M2 84H168" />
        <rect x="70" y="8" width="62" height="70" />
        <rect x="58" y="14" width="62" height="70" />
        <path d="M68 28h30M68 36h42M68 48h42M68 56h42M68 64h42M88 48v16" />
        <rect x="16" y="40" width="34" height="44" />
        <path d="M22 50h22M22 58h22M22 66h14" />
      </svg>
    </span>
  );
}

export type StepState = "done" | "current" | "todo";
/**
 * 段の状態（docs/36 §1）：済＝その段の必須がそろった／いま＝済でない最初の段／まだ＝その後。
 * 最後の段（中身・支払と請求）は主役のボタンで終える段なので、この画面では ✓ にしない（done に false を渡す）
 */
export function stepStates(done: boolean[]): StepState[] {
  const cur = done.findIndex((d) => !d);
  return done.map((d, i) => (d ? "done" : i === cur ? "current" : "todo"));
}

/** 段のカードへ移る（帯の段を押したとき）。見出しに移して読み上げにも伝える */
export function goStep(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  el.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
}

export function CreateBand({
  back,
  title,
  steps = [],
  states = [],
  chips,
  deco,
}: {
  /** 「‹ 戻り先」（#98：戻るボタンと同じ行き先の決め方＝useBackNav）。字は行き先の名前（長いと …） */
  back: BackNav;
  title: string;
  /** 段（作成画面）。工程表（#100）のように段の無い画面は省く */
  steps?: { id: string; label: string }[];
  states?: StepState[];
  /** 題の下のしるし（工程表の {customer}・工期・担当。#100） */
  chips?: ReactNode;
  deco: "house" | "building";
}) {
  // 家・建物の線画は現場力（field）の会社だけ。ほかは書類の絵（#142 段6 参考）
  const field = useModule("field");
  return (
    <header className="crband">
      <BandDeco kind={field ? deco : "docs"} />
      <Link className="crback" to={back.to} onClick={back.request} title={backTitle(back)} data-testid="create-back">
        <CrIcon k="back" />
        <span className="bkl">
          <span className="bkt">{back.label}</span>
          <span className="bksr">に戻る</span>
        </span>
      </Link>
      <h1>{title}</h1>
      {chips ? <div className="crchips">{chips}</div> : null}
      {steps.length ? <ol className="crsteps" role="list" aria-label="段">
        {steps.map((s, i) => (
          <li key={s.id}>
            {i ? <span className="crline" aria-hidden="true" /> : null}
            <button type="button" className="crstep" data-state={states[i]} aria-current={states[i] === "current" ? "step" : undefined} onClick={() => goStep(s.id)} data-testid={`band-step-${i + 1}`}>
              <span className="crdot">{states[i] === "done" ? <CrIcon k="check" /> : i + 1}</span>
              <span className="crsl">{s.label}</span>
              {states[i] === "done" ? <span className="sr">（済み）</span> : null}
            </button>
          </li>
        ))}
      </ol> : null}
    </header>
  );
}

/* ---------------- 段のカード ---------------- */
export function StepCard({
  id,
  n,
  title,
  state,
  open,
  action,
  help,
  children,
}: {
  id: string;
  n: number;
  title: string;
  state: StepState;
  /** 中身を出すか（まだの段でも、{customer}を選んだ後は出す。選ぶ前は見出しだけの淡いカード） */
  open: boolean;
  /** 見出しの右（「変える」など） */
  action?: ReactNode;
  /** 見出しの右の「？」 */
  help?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="crcard" id={id} data-state={state} data-open={open ? "" : undefined} aria-labelledby={`${id}-h`} data-testid={`step-${n}`}>
      <div className="crhead">
        <span className="crbadge" data-state={state} aria-hidden="true">
          {state === "done" ? <CrIcon k="check" /> : n}
        </span>
        <h2 id={`${id}-h`} tabIndex={-1}>
          {title}
          {state === "done" ? <span className="sr">（済み）</span> : null}
        </h2>
        {help}
        <span className="sp" />
        {action}
      </div>
      {open ? <div className="crbody">{children}</div> : null}
    </section>
  );
}

/* ---------------- 押せるカード ---------------- */
export function Choice({
  checked,
  onClick,
  title,
  sub,
  icon,
  square,
  stack,
  children,
  testId,
  label,
}: {
  checked: boolean;
  onClick: () => void;
  title: ReactNode;
  sub?: ReactNode;
  icon?: CIcon;
  /** アイコンを角丸の四角に（作り方） */
  square?: boolean;
  /** 名前の下に中身を広げる（請求タイミングの棒） */
  stack?: boolean;
  children?: ReactNode;
  testId?: string;
  /** 読み上げの名前（既定は中の文字） */
  label?: string;
}) {
  return (
    <button type="button" role="radio" aria-checked={checked} aria-label={label} className={"crchoice" + (stack ? " stack" : "")} onClick={onClick} data-testid={testId}>
      {icon ? (
        <span className={"crci" + (square ? " sq" : "")}>
          <CrIcon k={icon} />
        </span>
      ) : null}
      <span className="crct">
        <span className="crctt">{title}</span>
        {sub ? <span className="crcts">{sub}</span> : null}
        {children}
      </span>
      <span className="crradio" aria-hidden="true">
        <CrIcon k="check" />
      </span>
    </button>
  );
}

/** 点線のカード（「ほかの{project}　あと N件 ›」）。押すと全部出す */
export function MoreChoice({ children, onClick, testId }: { children: ReactNode; onClick: () => void; testId?: string }) {
  return (
    <button type="button" className="crmore" onClick={onClick} data-testid={testId}>
      <span>{children}</span>
      <CrIcon k="right" />
    </button>
  );
}

/* ---------------- 段1：{customer} ---------------- */
/** 頭文字の丸 */
export function Initial({ name, big, small }: { name: string; big?: boolean; /** タイルの 32px（#97） */ small?: boolean }) {
  return (
    <span className={"crav" + (big ? " big" : small ? " tile" : "")} aria-hidden="true">
      {partnerInitial(name) || "？"}
    </span>
  );
}

/** 選んだ{customer}：頭文字の丸（48px）＋名前＋敬称（小さく）＋「支払条件　…」 */
export function PickedClientRow({ partner, fallbackName }: { partner: Partner | null; fallbackName?: string | undefined }) {
  const name = partner?.name ?? fallbackName ?? "…";
  const terms = (partner?.paymentTerms ?? null) as PaymentTerms | null;
  return (
    <div className="crpicked" data-testid="picked-client">
      <Initial name={name} big />
      <span className="crpt">
        <span>
          <b>{name}</b> <small>{partner?.honorific ?? ""}</small>
        </span>
        {partner ? (
          <span className="muted">
            支払条件　{paymentTermsLabel(terms ?? DEFAULT_PAYMENT_TERMS)}
            {terms ? "" : "（既定）"}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/* ---------------- できあがり ---------------- */
/** 右の「できあがり」（1100px 以上）。入れたものがその場で写る。読み上げは不要なので aria-hidden */
export function PreviewAside({ title, icon, chip, children }: { title: string; icon: CIcon; chip?: string; children: ReactNode }) {
  return (
    <aside className="crside" aria-hidden="true" data-testid="preview">
      <div className="crsidehead">
        <CrIcon k={icon} />
        <span>{title}</span>
        <span className="sp" />
        {chip ? <span className="chip quiet">{chip}</span> : null}
      </div>
      {/* できあがりの紙はダークでもライトのまま（#191 の2回目） */}
      <LightArea>{children}</LightArea>
    </aside>
  );
}

/**
 * たたんだ形（1100px 未満）：小さな絵・1行目・2行目・「紙を見る ⌃」。押すと下から出る（PreviewSheet）。
 * 2行目が空なら出さない（#97：{customer}を選ぶ前は「できあがりの紙」とだけ）
 */
export function PreviewMini({ thumb, line1, line2, open, onToggle, buttonLabel, sheetId }: { thumb: ReactNode; line1: ReactNode; line2: ReactNode; open: boolean; onToggle: () => void; buttonLabel: string; sheetId: string }) {
  return (
    <div className="crmini" data-testid="preview-mini">
      <span className="crthumb" aria-hidden="true">
        {thumb}
      </span>
      <span className="crmt">
        <span className="l1" data-testid="preview-mini-l1">{line1}</span>
        {line2 ? (
          <span className="l2" data-testid="preview-mini-l2">
            {line2}
          </span>
        ) : null}
      </span>
      <button type="button" className="btn text" aria-expanded={open} aria-controls={sheetId} onClick={onToggle} data-testid="preview-toggle">
        {buttonLabel}
        <CrIcon k="up" />
      </button>
    </div>
  );
}

/** 下から出る紙（Esc・外を押す・もう一度押すで閉じる）。出入りは 200ms（動きを減らす設定では止まる） */
export function PreviewSheet({ id, open, onClose, title, children }: { id: string; open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const [shown, setShown] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (open) {
      setShown(true);
      setLeaving(false);
      return;
    }
    if (!shown) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShown(false);
      return;
    }
    setLeaving(true);
    const t = setTimeout(() => {
      setShown(false);
      setLeaving(false);
    }, 200);
    return () => clearTimeout(t);
  }, [open, shown]);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!shown) return null;
  return (
    <div className={"crscrim" + (leaving ? " out" : "")} onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="preview-sheet-scrim">
      <div className="crsheet" id={id} role="dialog" aria-label={title} data-testid="preview-sheet" data-notice-above="">
        <div className="crsheethead">
          <b>{title}</b>
          <span className="sp" />
          <button ref={closeRef} type="button" className="btn text" onClick={onClose}>
            閉じる
          </button>
        </div>
        <div aria-hidden="true">
          <LightArea>{children}</LightArea>
        </div>
      </div>
    </div>
  );
}

/* ---------------- 下のボタン ---------------- */
/** 左「やめる」（控えめ・大）、右に主役（塗り・大・右に →）。必須がそろうまで押せない。狭い画面（700px 未満）は主役が画面の下に付き、「やめる」は出さない */
export function CreateActions({ back, label, disabled, onClick, testId }: { /** 「やめる」の行き先（帯の「‹」と同じ。入れかけなら確かめる） */ back: BackNav; label: string; disabled: boolean; onClick: () => void; testId: string }) {
  return (
    <div className="cract" data-notice-above="">
      <Link className="btn quiet big crcancel" to={back.to} onClick={back.request}>
        取消
      </Link>
      <span className="sp" />
      <button type="button" className="btn primary big" disabled={disabled} onClick={onClick} data-testid={testId}>
        {label}
        <CrIcon k="arrow" />
      </button>
    </div>
  );
}

/** 今日（日本時間）を「2026年9月29日」／「9月29日」に */
export function todayJp(withYear = true): string {
  const d = new Date(Date.now() + 9 * 3600_000);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return withYear ? `${y}年${m}月${day}日` : `${m}月${day}日`;
}
